import { Toast } from 'antd-mobile';
import {
  IDoOneSentenceAsrRecognition,
  IOnOneSentenceAsrRecognitionStateChanged,
  IOpenPageForDeerPictureBookModel,
} from '../typings/js_bridge';

// 解密请求队列管理
class DecryptQueue {
  private queue: Array<{
    id: string;
    data: string;
    callback: (args: string) => void;
  }> = [];
  private isProcessing = false;
  private currentRequestId: string | null = null;

  add(data: string, callback: (args: string) => void): void {
    const id = `decrypt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.queue.push({ id, data, callback });
    this.processNext();
  }

  private processNext(): void {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const currentRequest = this.queue.shift()!;
    this.currentRequestId = currentRequest.id;

    this.processRequest(currentRequest);
  }

  private processRequest(request: {
    id: string;
    data: string;
    callback: (args: string) => void;
  }): void {
    const { id, data, callback } = request;

    // 设置全局回调函数，只处理当前请求
    window.getDecryptData = (decryptData: string) => {
      console.log('decryptData===', decryptData);

      // 确保只处理当前请求的回调
      if (this.currentRequestId === id) {
        try {
          callback(decryptData);
        } catch (error) {
          console.error('Decrypt callback error:', error);
        }

        // 处理完成，继续下一个
        this.isProcessing = false;
        this.currentRequestId = null;
        this.processNext();
      }

      return decryptData;
    };

    if (messageMethodIsSupport('nativeDecrypt')) {
      window.nativeDecrypt?.postMessage(data);

      // 设置超时处理，防止原生回调丢失
      setTimeout(() => {
        if (this.currentRequestId === id) {
          console.error('Native decrypt timeout for request:', id);
          this.isProcessing = false;
          this.currentRequestId = null;
          this.processNext();
        }
      }, 10000); // 10秒超时
    } else {
      Toast.show({
        content: '请升级APP到最新版本',
      });
      this.isProcessing = false;
      this.currentRequestId = null;
      this.processNext();
    }
  }
}

// 创建全局解密队列实例
const decryptQueue = new DecryptQueue();

export const JsBridge = {
  // 获取用户信息
  isInApp: (): boolean => {
    return messageMethodIsSupport('getUserInfo');
  },

  setNavigationBar: (data?: any) => {
    if (messageMethodIsSupport('setNavigationBar')) {
      window.setNavigationBar?.postMessage(data);
    }
  },

  openAlbumAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openAlbumAndUpload')) {
      window.openAlbumAndUpload?.postMessage(data);
    }
  },
  // 打开文件选择
  openFileAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openFileAndUpload')) {
      window.openFileAndUpload?.postMessage(data);
    }
  },

  // 获取用户信息
  getUserInfo: (data?: any) => {
    if (messageMethodIsSupport('getUserInfo')) {
      window.getUserInfo?.postMessage(data);
    }
  },

  // 更新用户信息
  updateUserInfo: (data?: any) => {
    if (messageMethodIsSupport('updateUserInfo')) {
      window.updateUserInfo?.postMessage(data);
    }
  },

  // 跳转到登录页面
  gotoLogin: (data?: any) => {
    if (messageMethodIsSupport('gotoLogin')) {
      window.gotoLogin?.postMessage(data);
    }
  },

  // 关闭webview
  closeWebview: (data?: any) => {
    if (messageMethodIsSupport('closeWebview')) {
      window.closeWebview?.postMessage(data);
    }
  },

  // 设置全局函数，用户手势或者物理按键回退后的回调
  setClickBackCallback: (fun: any) => {
    window['clickBack'] = () => {
      try {
        fun();
      } catch (error) {
        return false;
      }
      return true;
    };
  },

  // native 解密接口返参
  nativeDecrypt: (data: string, callback: (args: string) => void) => {
    decryptQueue.add(data, callback);
  },

  onVisibleChange: (callback: (result: boolean) => void) => {
    window.onVisibleChange = callback;
  },

  shareUrl: (data?: any) => {
    if (messageMethodIsSupport('shareUrl')) {
      window.shareUrl?.postMessage(data);
    }
  },

  // 分享 dataURL 图片
  shareDataUrlImage: (data?: any) => {
    if (messageMethodIsSupport('shareDataUrlImage')) {
      // @ts-ignore
      window.shareDataUrlImage?.postMessage(data);
    }
  },

  // 唤起APP分享
  shareWeb: (data?: any) => {
    if (messageMethodIsSupport('shareWeb')) {
      window.shareWeb?.postMessage(data);
    }
  },

  // APP内唤起微信客服
  openCustomerServiceChat: (data?: any) => {
    if (messageMethodIsSupport('openCustomerServiceChat')) {
      window.openCustomerServiceChat?.postMessage(data);
    }
  },

  //会员下单
  buyVip: (data?: any) => {
    if (messageMethodIsSupport('buyVip')) {
      window.buyVip?.postMessage(data);
    }
  },
  //app升级
  updateAppVersion: (data?: any) => {
    if (messageMethodIsSupport('updateAppVersion')) {
      window.updateAppVersion?.postMessage(data);
    }
  },

  openWebPage: (data?: any) => {
    if (messageMethodIsSupport('openWebPage')) {
      window.openWebPage?.postMessage(data);
    }
  },

  // 打开具体某个Native页面
  // {pageName: 'pageName', extra: {}}
  openPage: (data?: any) => {
    if (messageMethodIsSupport('openPage')) {
      window.openPage?.postMessage(data);
    }
  },

  sharePageFreeMembership: (data: string) => {
    if (messageMethodIsSupport('shareUrl')) {
      window.shareUrl?.postMessage(data);
    }
  },

  wxLogin: (data: any) => {
    if (messageMethodIsSupport('wxLogin')) {
      window.wxLogin?.postMessage(data);
    }
  },

  // 打开绘本，视频等
  openPageForDeerPictureBookModel: (data: IOpenPageForDeerPictureBookModel) => {
    if (messageMethodIsSupport('openPageForDeerPictureBookModel')) {
      window.openPageForDeerPictureBookModel?.postMessage(JSON.stringify(data));
    }
  },

  openVideoAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openVideoAndUpload')) {
      window.openVideoAndUpload?.postMessage(data);
    }
  },

  baiduAuthorize: (data?: any) => {
    if (messageMethodIsSupport('baiduAuthorize')) {
      window.baiduAuthorize?.postMessage(data);
    }
  },

  nativeLog: (key?: any, value?: any) => {
    var data = JSON.stringify({ key: key, value: value });
    if (messageMethodIsSupport('nativeLog')) {
      window.nativeLog?.postMessage(data);
    }
  },

  // 下载图片到相册
  downloadImageToGallery: (data?: any) => {
    if (messageMethodIsSupport('downloadImageToGallery')) {
      window.downloadImageToGallery?.postMessage(data);
    }
  },
  // 保存 dataURL 图片到相册
  saveDataUrlImageToGallery: (data?: any) => {
    if (messageMethodIsSupport('saveDataUrlImageToGallery')) {
      window.saveDataUrlImageToGallery?.postMessage(data);
    }
  },
  // 微信支付用户确认
  merchantTransfer: (data?: any) => {
    if (messageMethodIsSupport('merchantTransfer')) {
      window.merchantTransfer?.postMessage(data);
    }
  },
  // 一句话语音转文字
  doOneSentenceAsrRecognition: (data?: IDoOneSentenceAsrRecognition) => {
    if (messageMethodIsSupport('doOneSentenceAsrRecognition')) {
      window.doOneSentenceAsrRecognition?.postMessage(JSON.stringify(data));
    }
  },
  onOneSentenceAsrRecognitionStateChanged: (
    callback: (result: IOnOneSentenceAsrRecognitionStateChanged) => void
  ) => {
    window.onOneSentenceAsrRecognitionStateChanged = callback;
  },
};

// message 类的方法，直接挂在widow上, 只能调用APP方法，无法获取返回值
export function messageMethodIsSupport(methodName: string) {
  return typeof window !== 'undefined' && window?.[methodName as keyof Window];
}
